import { useState, useEffect } from "react";

// Hooks
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Store
import { showLoader } from "store/slice/UI";
import { clearChart } from "store/slice/charts/chartsSlice";
import { TNarrative, TPresetVcp } from "store/slice/Vcp/vcpSlice";
import { getInsightById, updateInsightChild } from "store/slice/Project/insights/insightsSlice";

// Types
import { LocationFrom } from "types/global";
import { NavButtonProps } from "components/HeaderNavigation/NavButton";

// Services
import { saveProjectByPathService, updateProjectByPathService } from "services";

// Enums
import { urlPaths } from "enums/urlPaths";

// Utils
import { isEmpty } from "lodash";
import { shallowEqual } from "react-redux";
import { buildParamsString } from "utils";
import {
    getInsightPeersPath,
    getInsightRelatedSltPath,
    getInsightRelatedVcpData,
} from "pages/ProjectDashboard/adapters/formattedData";
import { clearICDashboard } from "store/slice/Project/icDashboard/icDashboard.slice";

type useVcpNavActionsProps = (
    companyId: string | undefined,
    projectId: string | null,
    insightId: string | null,
    searchAiId: string | null,
    selectedPreSettedVcp: React.MutableRefObject<TPresetVcp | undefined>,
    selectedNarratives: TNarrative[],
    shouldContinue: () => boolean
) => {
    backButton: NavButtonProps;
    nextButton: NavButtonProps;
};

const useVcpNavActions: useVcpNavActionsProps = (
    companyId,
    projectId,
    insightId,
    searchAiId,
    selectedPreSettedVcp,
    selectedNarratives,
    shouldContinue
) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation() as unknown as LocationFrom;
    const [searchParams] = useSearchParams();

    const briefId = searchParams.get("briefId");

    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);

    const [buttonLabel, setbuttonLabel] = useState("button.next");
    const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;
    const relatedVcpData = getInsightRelatedVcpData(insightData)?.data;
    const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;
    const canGotoInsights = Boolean(relatedPeersPath && relatedSltPath && relatedVcpData?.id);

    // if isEditing
    useEffect(() => {
        canGotoInsights && setbuttonLabel("button.finish.refinement");
    }, [canGotoInsights]);

    const handleBack = () => {
        const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });
        const urlParams = `${companyId}?${queryParams}`;
        const backLocation =
            location.state?.from ||
            (canGotoInsights ? `${urlPaths.Projection}/${urlParams}` : `${urlPaths.Team}/${urlParams}`);
        navigate(backLocation, { state: { from: `${urlPaths.Vcp}/${urlParams}` } });
    };

    const handleNext = async () => {
        if (shouldContinue()) {
            return;
        }

        dispatch(showLoader(true));

        // Update VCP
        try {
            const payload = {
                ...(selectedPreSettedVcp.current?.id ? { preset: selectedPreSettedVcp.current?.id } : {}),
                narratives: selectedNarratives.map((narrative) => ({
                    type: narrative.id.toUpperCase(),
                    weight: narrative.weight,
                })),
            };

            if (!relatedVcpData?.id) {
                // Create VCP
                const { data: vcpData } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/insight_list/insight_${insightId}/vcp`,
                    data: payload,
                    echo: true,
                });

                if (!isEmpty(vcpData) && insightId) {
                    dispatch(updateInsightChild({ data: vcpData, id: Number(insightId), type: "PROJECT_VCP" }));
                }
            } else {
                // Update VCP
                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/insight_list/insight_${insightId}/vcp`,
                    data: payload,
                });

                const insightVCP = getInsightRelatedVcpData(insightData);

                if (insightId && insightVCP) {
                    const updatedData = {
                        ...insightVCP,
                        data: { ...insightVCP.data, ...payload },
                    };
                    dispatch(updateInsightChild({ data: updatedData, id: Number(insightId), type: "PROJECT_VCP" }));
                }
            }
        } catch (error) {
            console.error(error);
            dispatch(showLoader(false));
            return;
        }

        const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });
        const urlParams = `${companyId}?${queryParams}`;

        dispatch(clearChart());
        dispatch(clearICDashboard());
        navigate(`${urlPaths.LeadershipOverview}/${urlParams}`);
    };

    return {
        backButton: {
            onClick: handleBack,
        },
        nextButton: {
            translationId: buttonLabel,
            onClick: handleNext,
        },
    };
};

export default useVcpNavActions;
